import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { Eye, Users, Inbox, Percent, Smartphone } from "lucide-react";
import { useAdminApi } from "./useAdminApi";
import { PanelHeader } from "./LeadsPanel";

const COLORS = ["#00C9B7", "#10B981", "#F59E0B", "#38BDF8", "#F472B6", "#A3E635"];
const tip = { contentStyle: { background: "#131B21", border: "1px solid rgba(0,201,183,.3)", borderRadius: 12, color: "#fff" } };

const Stat = ({ icon: Icon, label, value, i }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="rounded-2xl bg-[#131B21] border border-[#00C9B7]/15 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <Icon size={18} className="text-[#00C9B7] mb-3" />
    <p className="text-3xl font-display">{value}</p>
    <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</p>
  </motion.div>
);

const Card = ({ title, children, className = "" }) => (
  <div className={`rounded-2xl bg-[#131B21] border border-white/5 p-5 ${className}`}><p className="text-xs uppercase tracking-widest text-slate-400 mb-4">{title}</p>{children}</div>
);

export default function AnalyticsPanel() {
  const api = useAdminApi();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/analytics?days=${days}`).then(setD); }, [days, api]);

  return (
    <div data-testid="analytics-panel">
      <PanelHeader title="Analytics" sub="Visitors, pages and leads tracked by your website.">
        <div className="flex gap-1 bg-[#131B21] rounded-xl p-1">
          {[7, 30, 90].map((n) => <button key={n} onClick={() => setDays(n)} className={`px-4 py-2 rounded-lg text-xs uppercase tracking-widest ${days === n ? "bg-[#00C9B7] text-[#031312] font-bold" : "text-slate-400"}`} data-testid={`analytics-range-${n}`}>{n}d</button>)}
        </div>
      </PanelHeader>
      {!d ? <p className="text-slate-500">Loading…</p> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat i={0} icon={Eye} label="Page views" value={d.totals.pageviews} />
            <Stat i={1} icon={Users} label="Visitors" value={d.totals.visitors} />
            <Stat i={2} icon={Inbox} label="Leads" value={d.totals.leads} />
            <Stat i={3} icon={Percent} label="Conversion" value={`${d.totals.conversion}%`} />
            <Stat i={4} icon={Smartphone} label="Mobile" value={`${d.totals.mobile_share}%`} />
          </div>
          <Card title="Traffic & leads over time">
            <div className="h-72">
              <ResponsiveContainer>
                <AreaChart data={d.daily}>
                  <defs><linearGradient id="v" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00C9B7" stopOpacity={0.5} /><stop offset="100%" stopColor="#00C9B7" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                  <Tooltip {...tip} />
                  <Area type="monotone" dataKey="views" stroke="#00C9B7" fill="url(#v)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" stroke="#38BDF8" fill="transparent" strokeWidth={2} />
                  <Area type="monotone" dataKey="leads" stroke="#F59E0B" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <div className="grid lg:grid-cols-3 gap-5">
            <Card title="Top pages" className="lg:col-span-1">
              <div className="h-64"><ResponsiveContainer><BarChart data={d.top_pages} layout="vertical"><XAxis type="number" hide /><YAxis type="category" dataKey="path" width={110} stroke="#94a3b8" fontSize={11} /><Tooltip {...tip} /><Bar dataKey="views" fill="#00C9B7" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer></div>
            </Card>
            <Card title="Traffic sources">
              <div className="h-64"><ResponsiveContainer><PieChart><Pie data={d.referrers} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>{d.referrers.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip {...tip} /></PieChart></ResponsiveContainer></div>
              <div className="flex flex-wrap gap-3 text-xs">{d.referrers.map((r, i) => <span key={r.name} className="inline-flex items-center gap-1"><i className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{r.name} ({r.value})</span>)}</div>
            </Card>
            <Card title="Leads by form">
              <div className="h-64"><ResponsiveContainer><PieChart><Pie data={d.lead_sources} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>{d.lead_sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip {...tip} /></PieChart></ResponsiveContainer></div>
              <div className="flex flex-wrap gap-3 text-xs">{d.lead_sources.map((r, i) => <span key={r.name} className="inline-flex items-center gap-1"><i className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{r.name} ({r.value})</span>)}</div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
